/**
 * The shapes the backend returns.
 *
 * Declared here rather than imported from `functions/` because the two are
 * deployed separately and a build-time path into another package couples them
 * in a way that has bitten this repository once already. The contract is the
 * callable's response, and a mismatch surfaces in the E2E tests that run
 * against the emulator with the real backend behind them.
 */

export type UserRole = "spontar_admin" | "owner" | "teacher" | "student";
export type Locale = "en" | "de" | "fr";

export interface User {
  uid: string;
  /** null only for a Spontar admin, who belongs to no institution. */
  institutionId: string | null;
  role: UserRole;
  displayName: string;
  /** Null for a player: they hold a device rather than an account. */
  username: string | null;
  /** Never set for a student — no student email address is ever collected. */
  email: string | null;
  locale: Locale;
  disabled: boolean;
  mustChangePassword: boolean;
  createdAt: string;
  lastActiveAt?: string;
}

export function isStaff(role: UserRole): boolean {
  return role === "owner" || role === "teacher";
}

export interface MeResponse {
  user: User;
}

export type LicenceTier = "solo" | "small" | "medium" | "large";
export type Segment = "studio" | "school";
export type Zone = "eu" | "us";
export type SessionMode = "online" | "offline";
export type AssessmentPreset = "ncas" | "aoao" | "epa" | "custom";

/**
 * The institution document, as a client reads it.
 *
 * `billing` is deliberately absent from this type even though the document has
 * it. A back-office page that needs billing asks the billing callable, which
 * returns a shape built for display; nothing in the shell should carry a Stripe
 * customer id around in memory because it happened to be on the same document.
 */
export interface Institution {
  id: string;
  name: string;
  country: string;
  segment: Segment;
  zone: Zone;
  locale: Locale;
  licenceTier: LicenceTier;
  teacherSeats: number;
  validUntil: string;
  assessmentPreset: AssessmentPreset;
  defaultSessionMode: SessionMode;
  retention: { operationalDays: number };
  createdAt: string;
  suspendedAt?: string;
}

// ── legal ───────────────────────────────────────────────────────────
export type DocType =
  | "tos"
  | "dpa"
  | "avv"
  | "ferpa_addendum"
  | "state_addendum"
  | "privacy"
  | "privacy_child"
  | "impressum"
  | "parental_consent_template"
  | "law25_addendum";

export interface LegalDocument {
  id: string;
  type: DocType;
  jurisdictions: string[];
  segments: Segment[];
  language: Locale;
  version: number;
  /** Blocks session creation when unaccepted. */
  required: boolean;
  title: string;
  publishedAt: string;
}

export type LegalDocumentStatus = LegalDocument & {
  accepted: boolean;
  /** The version this institution accepted, if any. It may be an older one. */
  acceptedVersion?: number;
};

export interface LegalStatus {
  documents: LegalDocumentStatus[];
  outstanding: LegalDocument[];
  /**
   * Required types with nothing published. **Our** failure, not the
   * institution's — reported so the owner is not left wondering what to sign.
   */
  gaps: DocType[];
}

export interface LegalBody {
  docId: string;
  locale: Locale;
  version: number;
  title: string;
  /** Sanitised on the server against an allow-list. */
  html: string;
  url: string;
}

// ── consent ─────────────────────────────────────────────────────────
export type ConsentMethod =
  | "paper_form"
  | "digital_signature"
  | "verbal_recorded"
  | "school_policy";

export const CONSENT_METHODS: readonly ConsentMethod[] = [
  "paper_form",
  "digital_signature",
  "verbal_recorded",
  "school_policy",
] as const;

export interface RosterEntry {
  uid: string;
  displayName: string;
  addedAt: string;
}

export type AgeBand = "u13" | "13-15" | "16-17" | "adult" | "mixed";

export interface Group {
  id: string;
  institutionId: string;
  name: string;
  ageBand: AgeBand;
  containsMinors: boolean;
  defaultMode: SessionMode;
  teacherUids: string[];
  roster: RosterEntry[];
  createdAt: string;
  archivedAt?: string;
}

export interface ConsentStatus {
  required: boolean;
  /** Named, not counted. "Three missing" is not something an owner can act on. */
  missing: RosterEntry[];
  recorded: number;
}

// ── billing, as the settings page needs it ──────────────────────────
export type VatTreatment = "charge" | "reverse_charge" | "outside_scope";

export interface BillingStatus {
  licenceTier: LicenceTier;
  teacherSeats: number;
  teacherSeatsUsed: number;
  validUntil: string;
  manualBilling: boolean;
  hasSubscription: boolean;
  /** Set when a payment failed and cleared when one succeeds. A flag, not a lock. */
  paymentFailedAt?: string | null;
  status?: string;
  cancelAtPeriodEnd?: boolean;
  vatId: string | null;
  vatIdValidatedAt: string | null;
  vatTreatment: VatTreatment;
  tiers: Record<string, number>;
}

// ── import ──────────────────────────────────────────────────────────
/** What reading an uploaded file produced. Nothing is interpreted yet. */
export interface ParsedUpload {
  headers: string[];
  rows: string[][];
  /** Present for spreadsheets, so a sheet picker can be offered. */
  sheetNames?: string[];
  /** Rows skipped before the header — title banners, blank lines. */
  skippedLeadingRows: number;
  fingerprint: string;
}

/**
 * Which column holds what.
 *
 * `displayName` is a pair when a school export splits the name across two
 * columns — the common German case, `Nachname` and `Vorname`. The pair is
 * ordered as the name should be READ, not as it appears in the file.
 */
export interface ColumnMap {
  displayName: number | [number, number];
  role?: number;
  group?: number;
  ageBand?: number;
}

export type MappingConfidence = "high" | "medium" | "low";

export interface MappingProposal {
  columnMap: ColumnMap;
  confidence: MappingConfidence;
  /** i18n keys, never prose. */
  notes: string[];
  /** Which mapper produced this. Shown, so a degradation is never silent. */
  source: "ai" | "heuristic";
}

export interface ReviewedRow {
  rowNumber: number;
  displayName: string;
  role: "teacher" | "student";
  groupName?: string;
  ageBand?: AgeBand;
  locale: Locale;
}

export interface ImportSkip {
  row: number;
  reasonKey: string;
}

/**
 * Somebody an import created.
 *
 * `credentials` is present for STAFF and absent for players, because a player
 * has none: they hold a device the institution enrolled and never sign in.
 * Optional rather than empty strings, so a caller that forgets to check gets a
 * type error instead of printing a slip with a blank password on it.
 */
export interface ImportedPerson {
  uid: string;
  displayName: string;
  role: "teacher" | "student";
  groupName?: string;
  credentials?: {
    username: string;
    /**
     * Shown once and never retrievable. It exists in the printed PDF and in the
     * Auth password hash, and nowhere else.
     */
    temporaryPassword: string;
  };
}

export interface CommitImportResult {
  created: ImportedPerson[];
  skipped: ImportSkip[];
  warnings: ImportSkip[];
  groupsCreated: string[];
}

export const MAX_UPLOAD_BYTES = 2 * 1024 * 1024;
export const MAX_IMPORT_ROWS = 1000;

export const AGE_BANDS: readonly AgeBand[] = ["u13", "13-15", "16-17", "adult", "mixed"] as const;

// ── devices ─────────────────────────────────────────────────────────
export interface DeviceEnrolment {
  /** Made on the device on its first boot, read over USB when it is added. */
  deviceId: string;
  publicKey: string;
  /**
   * What the label on the case says — "Set B, 07". Made from the kit's name
   * when the device is added over USB; typed by staff when it is registered
   * by hand.
   */
  deviceLabel: string;
  /** The kit whose network the device holds. Absent for one registered by hand. */
  kitId?: string;
  enrolledAt: string;
  revokedAt?: string;
  lastSeenAt?: string;
}

// ── kits ────────────────────────────────────────────────────────────
/** The routers the teacher's app can set up with one tap. */
export type RouterModel = "gl-mt3000";
export const ROUTER_MODELS: readonly RouterModel[] = ["gl-mt3000"] as const;

/**
 * A kit: a box for one room — its own Wi-Fi network, its devices, a case.
 *
 * The network's name and password are made on the server when the kit is
 * created and never chosen. They are shown here, to staff, for the router and
 * the iPad; they are never in a URL and never in an email.
 */
export interface Kit {
  id: string;
  name: string;
  /** `spontar-` and six lowercase letters or digits. */
  ssid: string;
  /** Sixteen letters and digits. */
  psk: string;
  routerModel: RouterModel;
  createdAt: string;
  createdBy: string;
}

export const KIT_NAME_MAX_LENGTH = 40;


// ── content ─────────────────────────────────────────────────────────
export type LearningGoal =
  | "status"
  | "accepting_offers"
  | "emotional_range"
  | "physicality"
  | "listening"
  | "text_work";

export type Pedagogy = "johnstone" | "spolin" | "boal" | "close" | "house";
export type Difficulty = 1 | 2 | 3 | 4 | 5;
export type RoleTag = "child_safe" | "adult_only" | "physical" | "speaking_heavy";
export type ScenarioStatus = "draft" | "published";
export type ExperienceLevel = "beginner" | "intermediate" | "advanced";

export interface CastRole {
  id: string;
  name: string;
  order: number;
  difficulty: Difficulty;
  tags: RoleTag[];
}

/**
 * One beat of the score.
 *
 * A situation and a feeling, **never a script**. Reading a line aloud is acting
 * and invites refusal; reacting to a situation is being a person. It also keeps
 * a beat short enough to be read standing up, by a twelve-year-old who does not
 * read well.
 */
/**
 * What a beat is. Absent means a situation — every beat written before
 * iteration 1. A line's words and a picture's caption live in `situation`.
 */
export type CueKind = "situation" | "line" | "image";

/** A picture on a beat, stored once at `scene-images/{sha256}.jpg`. */
export interface CueImage {
  sha256: string;
  width: number;
  height: number;
  bytes: number;
}

export interface Cue {
  id: string;
  roleId: string;
  order: number;
  situation: string;
  /** May be empty for lines (a delivery note) and pictures. */
  emotion: string;
  icon: string;
  kind?: CueKind;
  image?: CueImage;
}

export interface Constraint {
  id: string;
  label: string;
  description: string;
  source: Pedagogy;
}

export interface Scenario {
  id: string;
  institutionId: string;
  authorUid: string;
  editorUids: string[];
  status: ScenarioStatus;
  version: number;
  language: Locale;
  learningGoal: LearningGoal;
  title: string;
  synopsis: string;
  opening: string;
  minCast: number;
  maxCast: number;
  estimatedMinutes: number;
  roles: CastRole[];
  cues: Cue[];
  constraints: Constraint[];
  createdAt: string;
  /**
   * Moves on every write. The optimistic lock compares against this rather than
   * `version`, which only moves on publish — two people editing one unpublished
   * draft would both see version 0 and the later save would silently win.
   */
  updatedAt: string;
  publishedAt?: string;
  lastEditedBy?: string;
  archivedAt?: string;
}

export const SITUATION_MAX_LENGTH = 80;
export const OPENING_MAX_LENGTH = 1200;

export interface ValidationError {
  key: string;
  params?: Record<string, unknown>;
  /** Which beat or role it is about, when it is about one. */
  at?: string;
}

export interface ValidationResult {
  valid: boolean;
  errors: ValidationError[];
}

/** What the model returns, before anything has been saved. */
export type ScenarioDraft = Omit<
  Scenario,
  | "id"
  | "institutionId"
  | "authorUid"
  | "editorUids"
  | "status"
  | "version"
  | "createdAt"
  | "updatedAt"
>;

// ── sessions and review ─────────────────────────────────────────────
export type SessionState =
  | "lobby"
  | "casting"
  | "scene"
  | "debrief"
  | "ended"
  | "aborted";

export interface RegisterEntry {
  uid: string;
  displayName: string;
  present: boolean;
}

export interface CastAssignment {
  uid: string;
  roleId: string;
  difficulty: Difficulty;
}

export interface Run {
  id: string;
  scenarioId: string;
  scenarioVersion: number;
  constraintId?: string;
  cast: CastAssignment[];
  cues: Cue[];
  /** The role names the run was cast from, copied onto the run. */
  roles?: Array<{ id: string; name: string }>;
  currentCueIndex: number;
  startedAt: number;
  endedAt?: number;
  aborted?: boolean;
  /**
   * A scene typed during the lesson: `scenarioId` is "live", version 0, and
   * there is no scene to look up. Roles are the players, "Player 1"…
   */
  live?: true;
}

export interface Session {
  id: string;
  institutionId: string;
  groupId: string;
  teacherUid: string;
  mode: SessionMode;
  state: SessionState;
  code: string;
  seq: number;
  register: RegisterEntry[];
  /**
   * What the lesson was opened as: a scene's id, or `room` / `show` for the
   * two lessons that have no scene. Absent on lessons opened before it was
   * recorded.
   */
  scenarioId?: string;
  startedAt: string;
  endedAt?: string;
  syncedAt?: string;
}

/**
 * What the record keeps of the room (iteration 2).
 *
 * The review reads five kinds of event and no other: a prompt sent to an
 * actor who dried, a card asking for its line, a round played, a set played,
 * a draw. The payloads are the app's and read defensively — a field that is
 * not there is shown as nothing rather than crashing the page.
 */
export type ReviewEventType =
  | "prompt_sent"
  | "line_requested"
  | "round_played"
  | "set_played"
  | "jam_drawn";

export interface SessionEvent {
  id: string;
  type: ReviewEventType | string;
  at: number;
  runId?: string;
  uid?: string;
  roleId?: string;
  cueId?: string;
  payload?: Record<string, unknown>;
}

export type ScoreLevel = 1 | 2 | 3 | 4;

export interface SessionScore {
  uid: string;
  runId: string;
  level: ScoreLevel;
  preset: AssessmentPreset;
  recordedBy: string;
  recordedAt: string;
}

export interface SessionNotes {
  sessionId: string;
  text: string;
  updatedAt: string;
  updatedBy: string;
}

export interface SessionReview {
  session: Session;
  runs: Run[];
  scores: SessionScore[];
  notes: SessionNotes | null;
  /** The room's record, in order. Absent from a backend older than iteration 2. */
  events?: SessionEvent[];
}

/** One lesson's scripted runs and dries, for the line per student in a group's history. */
export interface GroupDriesSession {
  id: string;
  startedAt: string;
  scenarioId?: string;
  runs: Array<{
    id: string;
    scenarioId: string;
    scenarioVersion: number;
    beats: number;
    cast: Array<{ uid: string; roleId: string }>;
  }>;
  dries: SessionEvent[];
}

export interface GroupDries {
  sessions: GroupDriesSession[];
}

export const NOTES_MAX_LENGTH = 8000;

export interface SessionStatistics {
  id: string;
  institutionId: string;
  groupId: string;
  teacherUid: string;
  date: string;
  runCount: number;
  scenarioIds: string[];
  difficultyDistribution: Record<"1" | "2" | "3" | "4" | "5", number>;
  attendanceCount: number;
  rosterSize: number;
  durationMinutes: number;
  mode: SessionMode;
}

// ── reports ─────────────────────────────────────────────────────────
export interface DateRange {
  from: string;
  to: string;
}

export interface InstitutionReport {
  range: DateRange;
  sessionCount: number;
  runCount: number;
  activeGroups: number;
  activeTeachers: number;
  totalMinutes: number;
  averageAttendance: number;
  averageAttendanceRate: number;
  onlineSessions: number;
  offlineSessions: number;
}

export interface ContentReportRow {
  scenarioId: string;
  /** What the scene is called. Empty when it has since been deleted. */
  title: string;
  /** Every live run in the range, counted as one row with no title. */
  live?: true;
  /** Which sceneless row this is: a live scene, a room lesson, a show night. */
  kind?: "live" | "room" | "show";
  uses: number;
  groupsUsedIn: number;
  teachersUsedBy: number;
  /** A scenario a teacher comes back to is a scenario that worked. */
  repeatRate: number;
  averageSessionMinutes: number;
}

export interface ContentReport {
  range: DateRange;
  scenarios: ContentReportRow[];
}

export interface ActivityPoint {
  /** YYYY-MM. */
  month: string;
  sessionCount: number;
  runCount: number;
  activeGroups: number;
  totalMinutes: number;
}

export interface ActivityReport {
  range: DateRange;
  months: ActivityPoint[];
}

export interface TeacherActivityRow {
  teacherUid: string;
  sessionCount: number;
  runCount: number;
  totalMinutes: number;
  groupsTaught: number;
}

export interface TeacherActivityReport {
  range: DateRange;
  /** Unordered, deliberately. Usage is not performance. */
  teachers: TeacherActivityRow[];
}
